import type { ReactElement } from 'react';
import { Flex } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useBasket } from '../../../../bff-api/baskets/useBasket';
import { usePaymentMethods } from '../../../../bff-api/baskets/usePaymentMethods';
import { useCustomerGet } from '../../../../bff-api/customers/useCustomerGet';
import { ApplePayButton } from '../../../../components/apple-pay-button';
import { BasicError } from '../../../../components/basic-error/BasicError';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { PayPalButton } from '../../../../components/paypal-button';
import { SafoniaWarningCart } from '../../../../components/safonia-warning/SafoniaWarningCart';
import { SoftLink } from '../../../../components/soft-link/SoftLink';
import { isRegistered } from '../../../../contexts/utils/customer-utils';
import { useGTM, useSearchParams } from '../../../../hooks';
import { Button } from '../../../../ui-components';
import { isClient } from '../../../../utils/utils';
import { PageUrl } from '../../../utils/page-url';
import { useCartValidation } from '../../hooks/useCartValidation';

type Props = {
    id: string;
};

export const CartButtons = ({ id }: Props): ReactElement => {
    const { onCheckout } = useGTM();
    const query = useSearchParams();
    const {
        data: paymentMethods,
        isError: isPaymentMethodsError,
        error: paymentMethodsError,
    } = usePaymentMethods();
    const hasBlockingError = !!useCartValidation().length;
    const {
        data: customer,
        isError: isCustomerGetError,
        error: customerGetError,
    } = useCustomerGet();
    const { data: basket, isError: isBasketError, error: basketError } = useBasket();

    if (isBasketError || isPaymentMethodsError || isCustomerGetError) {
        throw basketError ?? paymentMethodsError ?? customerGetError;
    }

    const isSafoniaRestricted = (query.get('isSafoniaRestricted') ?? '') === 'true';
    const checkoutUrl = isRegistered(customer) ? PageUrl.CHECKOUT_SHIPPING : PageUrl.CHECKOUT_LOGIN;
    const checkoutButtonVariant =
        isClient && window.rewardsMessaging === 'PDP&CART' ? 'primary' : 'primary-black';

    return (
        <Flex data-testid={id} direction="column" gap={4}>
            <ConditionalRender
                visibility={
                    !!(
                        !hasBlockingError &&
                        paymentMethods &&
                        (paymentMethods.payPal.eligible || paymentMethods.applePay.eligible)
                    )
                }
            >
                <Flex width="100%" gap={4}>
                    {paymentMethods?.payPal.eligible ? (
                        <PayPalButton
                            id={`${id}-paypal`}
                            location="cart"
                            disabled={hasBlockingError}
                            paypalConfig={paymentMethods?.payPal.config}
                        />
                    ) : null}

                    {paymentMethods?.applePay.eligible ? (
                        <ApplePayButton
                            step="cart"
                            isDisabled={hasBlockingError}
                            params={{
                                isFromPWA: 'true',
                                source: 'cart',
                            }}
                        />
                    ) : null}
                </Flex>
            </ConditionalRender>

            <SoftLink
                isDisabled={hasBlockingError}
                to={checkoutUrl}
                data-testid={`${id}-checkout-link`}
                aria-label="Checkout"
            >
                <Button
                    id={`${id}-checkout`}
                    variant={checkoutButtonVariant}
                    onClick={onCheckout}
                    isDisabled={hasBlockingError}
                    wide
                >
                    <FormattedMessage id="checkout" defaultMessage="Checkout" />
                </Button>
            </SoftLink>

            {isSafoniaRestricted ? <SafoniaWarningCart /> : null}

            {hasBlockingError || basket?.c_paypalErrorMessage ? (
                <BasicError
                    options={{
                        alertDataTestId: 'cart-order-summary-global-error-container',
                        errorMessageDataTestId: 'cart-order-summary-global-error',
                    }}
                >
                    {basket?.c_paypalErrorMessage || (
                        <FormattedMessage
                            id="cart-order-summary.global-error"
                            defaultMessage="There is at least one error in the form, please correct them before continuing to the next step."
                        />
                    )}
                </BasicError>
            ) : null}
        </Flex>
    );
};
